import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sprout, MessageCircle, Expand } from "lucide-react";
import PageHero from "../components/PageHero";
import Lightbox from "../components/Lightbox";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, OTHER_PRODUCTS, waLink } from "../mock/mock";

const OtherProducts = () => {
  const [box, setBox] = useState({ open: false, images: [], title: "", index: 0 });
  const openGallery = (p) => setBox({ open: true, images: p.gallery, title: p.name, index: 0 });

  return (
    <div className="bg-noir">
      <Seo
        title="Oud Craft — Oud Jewellery, Carved Showpieces & Inoculum | Meilleur"
        description="Beyond raw material — oud jewellery, carved agarwood showpieces, bakhoor chips, and a proprietary inoculum programme that raises resin yield for partner farmers."
        path="/other-products"
        keywords="oud jewellery, agarwood showpieces, carved oud, bakhoor chips, agarwood inoculation, oud inoculum, oud crafts"
      />
      <PageHero eyebrow="Beyond Raw Material" title="Oud" accent="Craft" image={IMAGES.carvedArt} crumbs={["Collections", "Oud Craft"]} />

      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <Reveal><SectionHeading eyebrow="Other Oud Products" title="The full versatility" accent="of oud" /></Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light">
                Exploring the full versatility of oud — from fragrant incense to jewellery and decorative showpieces —
                crafted for trade partners and retail collaborators alike.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {OTHER_PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 110}>
                <div className="lux-card group h-full overflow-hidden">
                  <button onClick={() => openGallery(p)} className="img-zoom h-72 w-full block relative rounded-none" aria-label={`View ${p.name} gallery`}>
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-[rgba(14,58,43,0)] group-hover:bg-[rgba(14,58,43,0.35)] transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-[var(--cream)] text-xs tracking-[0.16em] uppercase border border-[rgba(245,240,230,0.5)] px-4 py-2">
                        <Expand size={14} /> View Gallery
                      </span>
                    </span>
                  </button>
                  <div className="p-8">
                    <h3 className="font-serif-display text-2xl mb-3 group-hover:text-[var(--gold-light)] transition-colors">{p.name}</h3>
                    <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{p.desc}</p>
                    <a href={waLink(p.name)} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-[var(--gold)] text-xs tracking-[0.2em] uppercase hover:text-[var(--gold-light)] transition-colors">
                      <MessageCircle size={14} /> Enquire on WhatsApp
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inoculum programme */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="img-zoom relative">
                <img src={IMAGES.forest} alt="Aquilaria plantation" className="w-full h-[500px] object-cover" />
                <div className="absolute inset-0 border border-[var(--line)]" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <Sprout size={30} className="text-[var(--gold)] mb-6" />
              <SectionHeading eyebrow="Our Inoculum Programme" title="Raising yield for every" accent="farmer we work with" />
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light max-w-2xl">
                Inoculation is the science of triggering resin formation inside a healthy Aquilaria tree — the single
                biggest driver of how much, and how valuable, a plantation's oud yield ultimately becomes. Our in-house
                service and inoculum supply is deployed across our own plantations and made available to partner farmers.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-8">
                {[
                  { v: "10,000", l: "Trees Inoculated to Date" },
                  { v: "₹40 Cr", l: "Estimated Value" },
                  { v: "On Request", l: "Technical Field Support" },
                ].map((s) => (
                  <div key={s.l} className="border-l border-[var(--line)] pl-4">
                    <div className="font-serif-display text-3xl gold-text">{s.v}</div>
                    <div className="text-[0.66rem] tracking-[0.14em] uppercase text-[var(--ivory-muted)] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-28 text-center">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow mb-6">Trade & Collaboration</p>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-8">Partner with our <span className="gold-text italic">craft</span></h2>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href={waLink("Oud Craft — jewellery, showpieces & inoculum")} target="_blank" rel="noopener noreferrer" className="btn-gold">
                <MessageCircle size={15} /> WhatsApp Enquiry
              </a>
              <Link to="/contact" className="btn-ghost">Get in Touch <ArrowRight size={15} /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Lightbox
        open={box.open}
        images={box.images}
        title={box.title}
        index={box.index}
        onClose={() => setBox({ ...box, open: false })}
      />
    </div>
  );
};

export default OtherProducts;
